<script setup>
import { onMounted, ref, computed, watch } from "vue";
import { useInscripcionTours } from "@/store/inscripcionTours";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import Swal from "sweetalert2";
import { getTorneoActivo, getCategoriasPeso } from "@/utils/api_backend/";
import TerminosYCondiciones from "@/components/Torneo/TerminosYCondiciones.vue";

const emit = defineEmits(["next-step", "prev-step"]);
const store = useInscripcionTours();

const props = defineProps({
  mostrarTerminos: Boolean,
});
const emitToggleTerminos = (val) => emit("toggle-terminos", val);

const categorias = ref([]);
const cargandoCategorias = ref(false);
const showError = ref(false);
const edadInvalida = ref(false);

onMounted(async () => {
  try {
    cargandoCategorias.value = true;
    const torneo = await getTorneoActivo();
    if (!torneo) return;
    categorias.value = await getCategoriasPeso(torneo.id);
  } catch (error) {
    console.error("Error cargando categorías:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron cargar las categorías.",
    });
  } finally {
    cargandoCategorias.value = false;
  }
});

// Opciones
const opcionesModalidad = [
  { label: "Gi", value: "gi" },
  { label: "No-Gi", value: "nogi" },
];

const opcionesGenero = [
  { label: "Masculino", value: "H" },
  { label: "Femenino", value: "M" },
];

const cintasAdulto = [
  { label: "Blanca", value: "blanca" },
  { label: "Azul", value: "azul" },
  { label: "Morada", value: "morada" },
  { label: "Café", value: "cafe" },
  { label: "Negra", value: "negra" },
];

const cintasJuvenil = [
  { label: "Blanca-Amarilla", value: "blanca_amarilla" },
  { label: "Amarilla", value: "amarilla" },
  { label: "Amarilla-Naranja", value: "amarilla_naranja" },
  { label: "Naranja", value: "naranja" },
  { label: "Naranja-Verde", value: "naranja_verde" },
  { label: "Verde", value: "verde" },
];

// Edad calculada
const edad = computed(() => {
  if (!store.fechaNacimiento) return null;
  const hoy = new Date();
  const nac = new Date(store.fechaNacimiento);
  let e = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) e--;
  return e;
});

const esJuvenil = computed(() => edad.value !== null && edad.value < 18);

const cintasDisponibles = computed(() => {
  if (edad.value === null || edad.value < 5) return [];
  return esJuvenil.value ? cintasJuvenil : cintasAdulto;
});

// Categorías filtradas por género y modalidad
const categoriasDisponibles = computed(() => {
  if (!store.genero || !store.matchPreference || edad.value === null) return [];

  // Determinar grupo_edad según fecha de nacimiento
  const grupoEdad = esJuvenil.value ? "juvenil" : "adulto";

  return categorias.value
    .filter(
      (c) =>
        c.genero === store.genero &&
        c.modalidad === store.matchPreference &&
        c.grupo_edad === grupoEdad // 👈 filtro por edad
    )
    .sort((a, b) => a.orden - b.orden);
});

const labelCategoria = (cat) => {
  const nombres = {
    rooster: "Rooster",
    light_feather: "Light Feather",
    feather: "Feather",
    light: "Light",
    middle: "Middle",
    medium_heavy: "Medium Heavy",
    heavy: "Heavy",
    super_heavy: "Super Heavy",
    ultra_heavy: "Ultra Heavy",
  };
  const div = nombres[cat.division] ?? cat.division;
  const peso = cat.peso_max_kg ? `≤ ${cat.peso_max_kg} kg` : "Open";
  const grupo = cat.grupo_edad === "juvenil" ? " · Juvenil" : "";
  return `${div} — ${peso}${grupo}`;
};

watch(
  () => store.fechaNacimiento,
  () => {
    edadInvalida.value = edad.value !== null && edad.value < 5;
    store.rangoCinta = null;
  }
);

// Resetear categoría si cambia género o modalidad
watch([() => store.genero, () => store.matchPreference], () => {
  store.categoriaPeso = null;
});

async function continuar() {
  showError.value = true;

  if (!store.fechaNacimiento || edad.value < 5) {
    Swal.fire({
      icon: "warning",
      title: "Edad mínima requerida",
      text: "El participante debe tener al menos 5 años.",
      confirmButtonColor: "#007bff",
    });
    return;
  }

  const faltantes = [
    store.rangoCinta,
    store.genero,
    store.matchPreference,
    store.categoriaPeso,
    store.academia,
  ].some((v) => !v);
  if (faltantes) {
    Swal.fire({
      icon: "warning",
      title: "Campos incompletos",
      text: "Completa todos los campos antes de continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }

  if (!store.aceptaTerminos) {
    Swal.fire({
      icon: "info",
      title: "Términos y condiciones",
      text: "Debes aceptar los términos para continuar.",
      confirmButtonColor: "#007bff",
    });
    return;
  }

  emit("next-step");
}
</script>

<template>
  <!-- Vista términos -->
  <div v-if="props.mostrarTerminos">
    <TerminosYCondiciones />
    <div class="text-end mt-3">
      <button class="btn-nav btn-secondary-nav" @click="emitToggleTerminos(false)">
        <i class="bi bi-arrow-left"></i> Volver al formulario
      </button>
    </div>
  </div>

  <!-- Vista formulario -->
  <form v-else class="step-container" @submit.prevent="continuar">
    <!-- Fecha nacimiento + Género -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Fecha de nacimiento <span class="req">*</span></label>
        <input
          type="date"
          class="field-input"
          :class="{
            'field-error': showError && (!store.fechaNacimiento || edadInvalida),
          }"
          v-model="store.fechaNacimiento"
        />
        <span v-if="edadInvalida" class="error-msg">Edad mínima: 5 años</span>
        <span v-if="edad !== null && !edadInvalida" class="hint-msg">
          {{ edad }} años · {{ esJuvenil ? "Juvenil" : "Adulto" }}
        </span>
      </div>
      <div class="field-group">
        <label class="field-label">Género <span class="req">*</span></label>
        <v-select
          v-model="store.genero"
          :options="opcionesGenero"
          :reduce="(o) => o.value"
          label="label"
          :clearable="false"
          placeholder="Selecciona"
          class="custom-select"
          :class="{ 'select-error': showError && !store.genero }"
        />
      </div>
    </div>

    <!-- Cinta + Modalidad -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Cinta <span class="req">*</span></label>
        <v-select
          v-model="store.rangoCinta"
          :options="cintasDisponibles"
          :reduce="(o) => o.value"
          label="label"
          :clearable="false"
          :disabled="cintasDisponibles.length === 0"
          placeholder="Selecciona tu cinta"
          class="custom-select"
          :class="{ 'select-error': showError && !store.rangoCinta }"
        />
        <span
          v-if="cintasDisponibles.length === 0 && store.fechaNacimiento"
          class="hint-msg"
        >
          Ingresa tu fecha de nacimiento primero
        </span>
      </div>
      <div class="field-group">
        <label class="field-label">Modalidad <span class="req">*</span></label>
        <v-select
          v-model="store.matchPreference"
          :options="opcionesModalidad"
          :reduce="(o) => o.value"
          label="label"
          :clearable="false"
          placeholder="Gi o No-Gi"
          class="custom-select"
          :class="{ 'select-error': showError && !store.matchPreference }"
        />
      </div>
    </div>

    <!-- Categoría de peso + Academia -->
    <div class="form-row-2">
      <div class="field-group">
        <label class="field-label">Categoría de peso <span class="req">*</span></label>
        <v-select
          v-model="store.categoriaPeso"
          :options="categoriasDisponibles"
          :get-option-label="labelCategoria"
          :clearable="false"
          :loading="cargandoCategorias"
          :disabled="categoriasDisponibles.length === 0"
          placeholder="Selecciona género y modalidad primero"
          class="custom-select"
          :class="{ 'select-error': showError && !store.categoriaPeso }"
        />
        <span
          v-if="
            categoriasDisponibles.length === 0 && store.genero && store.matchPreference
          "
          class="hint-msg"
        >
          No hay categorías disponibles para esta combinación
        </span>
      </div>
      <div class="field-group">
        <label class="field-label">Academia / Equipo <span class="req">*</span></label>
        <input
          type="text"
          class="field-input"
          :class="{ 'field-error': showError && !store.academia }"
          v-model="store.academia"
          placeholder="Ej. Evolve Jiu-Jitsu"
        />
      </div>
    </div>

    <!-- Términos y condiciones -->
    <div class="terminos-row">
      <label class="terminos-label">
        <input type="checkbox" v-model="store.aceptaTerminos" class="terminos-check" />
        <span>
          He leído y acepto los
          <a href="#" @click.prevent="emitToggleTerminos(true)" class="terminos-link">
            Términos y Condiciones
          </a>
        </span>
      </label>
    </div>

    <!-- Navegación -->
    <div class="nav-row">
      <button type="button" class="btn-nav btn-secondary-nav" @click="$emit('prev-step')">
        <i class="bi bi-arrow-left"></i>
        <span>Atrás</span>
      </button>
      <a href="/" class="btn-nav btn-ghost-nav">
        <i class="bi bi-house-fill"></i>
        <span>Inicio</span>
      </a>
      <button type="submit" class="btn-nav btn-primary-nav">
        <span>Siguiente</span>
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
  </form>
</template>

<style scoped>
.step-container {
  padding: 0.5rem 0;
}

.step-title {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 1.5rem;
}
.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #007bff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}
.step-title h5 {
  font-weight: 700;
  color: #1a1a2e;
  font-size: 1.05rem;
}
.step-sub {
  margin: 0;
  font-size: 0.78rem;
  color: #888;
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
.form-row-1 {
  margin-bottom: 16px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #444;
}
.req {
  color: #dc3545;
}

.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #333;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
}
.field-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.field-error {
  border-color: #dc3545 !important;
}
.error-msg {
  font-size: 0.72rem;
  color: #dc3545;
}
.hint-msg {
  font-size: 0.72rem;
  color: #888;
}

.custom-select {
  font-size: 0.9rem;
}
:deep(.vs__dropdown-toggle) {
  border: 1.5px solid #d8d8d8;
  border-radius: 8px;
  padding: 4px 8px;
}
:deep(.vs__dropdown-toggle:focus-within) {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
}
.select-error :deep(.vs__dropdown-toggle) {
  border-color: #dc3545;
}

.terminos-row {
  margin: 20px 0 8px;
}
.terminos-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #444;
}
.terminos-check {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  accent-color: #007bff;
  flex-shrink: 0;
}
.terminos-link {
  color: #007bff;
  text-decoration: underline;
}

.nav-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
.btn-nav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
}
.btn-primary-nav {
  background: #007bff;
  color: #fff;
}
.btn-primary-nav:hover {
  background: #0060df;
}
.btn-secondary-nav {
  background: #f0f0f0;
  color: #555;
}
.btn-secondary-nav:hover {
  background: #e0e0e0;
}
.btn-ghost-nav {
  background: transparent;
  color: #888;
  border: 1.5px solid #ddd;
}
.btn-ghost-nav:hover {
  border-color: #bbb;
  color: #555;
}

@media (max-width: 600px) {
  .form-row-2 {
    grid-template-columns: 1fr;
  }
}
</style>
